/**
 * StudioOpsCenter — Recycle Bin + Alerts Engine + Activity Timeline.
 * Three tabs in one page so admins have a single place to triage pipeline issues.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Trash2, AlertTriangle, Clock, RotateCcw, Sparkles, Loader2, ArrowRight, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StudioOpsCenter() {
  const [tab, setTab] = useState("alerts");
  return (
    <div data-testid="studio-ops-center" style={{ background: "#F8FAFC", minHeight: "100vh", color: "#0F172A", padding: 24 }}>
      <header style={{ maxWidth: 1400, margin: "0 auto" }}>
        <span className="ai-seo-eyebrow"><AlertTriangle size={14}/> Triage Hub</span>
        <h1 style={{ fontSize: 30, margin: "8px 0 4px", fontWeight: 800 }}>Pipeline Triage</h1>
        <p style={{ color: "#64748B", margin: 0 }}>Alerts engine · activity timeline · recycle bin — find and fix workflow gaps fast.</p>
        <nav style={{ display: "flex", gap: 6, marginTop: 18 }}>
          {[
            { key: "alerts",   label: "Alerts",         icon: <AlertTriangle size={14}/> },
            { key: "activity", label: "Activity feed",  icon: <Clock size={14}/> },
            { key: "trash",    label: "Recycle bin",    icon: <Trash2 size={14}/> },
          ].map((t) => (
            <button
              key={t.key} onClick={() => setTab(t.key)}
              data-testid={`ops-tab-${t.key}`}
              style={{
                padding: "8px 14px", borderRadius: 10, border: 0, cursor: "pointer",
                background: tab === t.key ? "#EDE9FE" : "#FFFFFF",
                color: tab === t.key ? "#0F172A" : "#64748B",
                display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13,
              }}
            >{t.icon} {t.label}</button>
          ))}
        </nav>
      </header>

      <main style={{ maxWidth: 1400, margin: "20px auto 0" }}>
        {tab === "alerts" && <AlertsTab/>}
        {tab === "activity" && <ActivityTab/>}
        {tab === "trash" && <TrashTab/>}
      </main>
    </div>
  );
}

/* ─── Alerts ─────────────────────────────────────────────────────────────── */
function AlertsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoFixing, setAutoFixing] = useState(false);

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/api/studio/crm/alerts`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load alerts"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const autoFix = async () => {
    const criticalIds = (data?.alerts || []).filter((a) => a.severity === "critical").map((a) => a.content_id);
    if (!criticalIds.length) { toast.error("No critical breaches to fix"); return; }
    setAutoFixing(true);
    try {
      const r = await axios.post(
        `${API}/api/studio/crm/auto-reassign`,
        { content_ids: criticalIds },
        { withCredentials: true },
      );
      toast.success(`Reassigned ${r.data.reassigned} task(s) to the least-loaded teammates`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fix failed");
    } finally { setAutoFixing(false); }
  };

  if (loading) return <div className="ai-seo-eyebrow"><Loader2 size={12} className="animate-spin"/> Scanning channels…</div>;
  if (!data) return null;

  const palette = { critical: "#EF4444", warning: "#F59E0B", info: "#3B82F6" };
  return (
    <div data-testid="ops-alerts">
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        {Object.entries(data.by_severity).map(([sev, n]) => (
          <div key={sev} data-testid={`ops-alerts-${sev}`} style={{ padding: "10px 14px", borderRadius: 10, background: "#FFFFFF", border: `1px solid ${palette[sev]}33` }}>
            <small style={{ color: "#64748B", textTransform: "capitalize" }}>{sev}</small>
            <div style={{ fontSize: 22, fontWeight: 800, color: palette[sev] }}>{n}</div>
          </div>
        ))}
        <small style={{ alignSelf: "center", color: "#64748B" }}>scanned {data.scanned_channels} channel(s)</small>
        {data.by_severity?.critical > 0 && (
          <button
            onClick={autoFix} disabled={autoFixing}
            data-testid="ops-alerts-autofix"
            className="studio-btn-primary"
            style={{ marginLeft: "auto", padding: "8px 14px", fontSize: 12 }}
            title="Auto-reassign every critical-SLA-breach row to the least-loaded teammate"
          >
            {autoFixing ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>} Auto-fix critical ({data.by_severity.critical})
          </button>
        )}
      </div>
      {data.alerts.length === 0 ? (
        <div style={{ padding: 28, textAlign: "center", color: "#64748B", border: "1px dashed #E2E8F0", borderRadius: 12 }}>
          🟢 No SLA breaches detected.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {data.alerts.map((a, i) => (
            <div key={i} data-testid={`ops-alert-${a.content_id}`} style={{ padding: 12, borderRadius: 10, background: "#FFFFFF", borderLeft: `3px solid ${palette[a.severity]}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <strong>{a.title}</strong>
                <small style={{ display: "block", color: "#64748B", marginTop: 2 }}>{a.message}</small>
              </div>
              <Link to={`/studio/channel/${a.channel_id}`} className="studio-link" data-testid={`ops-alert-open-${a.content_id}`}>
                Open <ArrowRight size={11}/>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Activity ───────────────────────────────────────────────────────────── */
function ActivityTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/api/studio/crm/activity?limit=80`, { withCredentials: true })
      .then((r) => setEvents(r.data.events || []))
      .catch((e) => toast.error("Failed"))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="ai-seo-eyebrow"><Loader2 size={12} className="animate-spin"/> Loading…</div>;
  return (
    <ul data-testid="ops-activity" style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
      {events.map((e, i) => (
        <li key={i} data-testid={`ops-activity-${i}`} style={{ padding: 10, borderRadius: 8, background: "#FAFAFA", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <div style={{ flex: 1, fontSize: 13 }}>
            <strong>{e.action}</strong>
            {e.content_title && <small style={{ display: "block", color: "#64748B" }}>{e.content_title}</small>}
          </div>
          <small style={{ color: "#94A3B8" }}>{e.ts ? new Date(e.ts).toLocaleString() : ""}</small>
        </li>
      ))}
      {!events.length && <li style={{ color: "#64748B", padding: 16 }}>No activity yet.</li>}
    </ul>
  );
}

/* ─── Recycle Bin ────────────────────────────────────────────────────────── */
function TrashTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/studio/ops/content/trash`, { withCredentials: true });
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (id) => {
    setRestoring(id);
    try {
      await axios.post(`${API}/api/studio/ops/content/restore/${id}`, {}, { withCredentials: true });
      toast.success("Restored");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setRestoring(null); }
  };

  if (loading) return <div className="ai-seo-eyebrow"><Loader2 size={12} className="animate-spin"/> Loading trash…</div>;
  if (!items.length) {
    return <div style={{ padding: 28, textAlign: "center", color: "#64748B", border: "1px dashed #E2E8F0", borderRadius: 12 }}>Recycle bin is empty.</div>;
  }
  return (
    <table data-testid="ops-trash-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: "left", borderBottom: "2px solid #E2E8F0" }}>
          <th style={{ padding: 10 }}>Title</th>
          <th style={{ padding: 10 }}>Channel</th>
          <th style={{ padding: 10 }}>Stage</th>
          <th style={{ padding: 10 }}>Deleted at</th>
          <th style={{ padding: 10, textAlign: "right" }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it._id} data-testid={`ops-trash-${it._id}`} style={{ borderBottom: "1px dashed #F1F5F9" }}>
            <td style={{ padding: 10 }}><strong>{it.title}</strong></td>
            <td style={{ padding: 10, color: "#64748B" }}>{it.channel_id}</td>
            <td style={{ padding: 10 }}><span className="studio-pill studio-pill--sm">{it.stage}</span></td>
            <td style={{ padding: 10, color: "#64748B" }}>{it.deleted_at ? new Date(it.deleted_at).toLocaleString() : "—"}</td>
            <td style={{ padding: 10, textAlign: "right" }}>
              <button onClick={() => restore(it._id)} disabled={restoring === it._id} data-testid={`ops-trash-restore-${it._id}`} className="studio-link">
                {restoring === it._id ? <Loader2 size={11} className="animate-spin"/> : <RotateCcw size={11}/>} Restore
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
